/**
 * O que as telas novas do painel pedem ao servidor (spec 096).
 *
 * Toda resposta passa pelo schema Zod antes de virar estado — quebrar na borda,
 * com o nome do campo, é mais barato que renderizar `undefined`.
 *
 * As funções recebem `organizationId` explicitamente. Não há organização
 * "corrente" implícita aqui de propósito: a fronteira de isolamento é o
 * argumento, e quem chama tem de dizer qual escritório está olhando.
 */
import api from './api'
import {
  type AgenteDeAtendimento,
  agenteDeAtendimentoSchema,
  type Aplicabilidade,
  aplicabilidadeSchema,
  type Atendimento,
  atendimentoSchema,
  type AttendanceListResponse,
  attendanceListResponseSchema,
  type BlueprintVersao,
  blueprintVersaoSchema,
  type CaixasDaConta,
  caixasResponseSchema,
  type Cascata,
  cascataSchema,
  type Cenario,
  cenarioSchema,
  type Conversa,
  type Conversao,
  conversaoSchema,
  conversaSchema,
  type ExecucaoAberta,
  execucaoAbertaSchema,
  type Fatia,
  fatiaSchema,
  type Motivo,
  motivoSchema,
  type OrganizationCostSummary,
  organizationCostSummarySchema,
  type OrganizationExecutionList,
  organizationExecutionListSchema,
  type Pergunta,
  perguntaSchema,
  type Ponto,
  pontoSchema,
  type TimeDoCanal,
  timeDoCanalSchema,
  type Validacao,
  validacaoSchema,
  type VinculoDeCaixa,
  vinculoDeCaixaSchema,
} from '@/schemas/painel'
import { z } from 'zod'

/** Janela de consulta, em dias contados para trás a partir de hoje. */
export interface Janela {
  dias: number
  /** Spec 157 (FR-013): um atendimento só; nulo soma todos. */
  agentId?: string | null
}

/** Os parâmetros de uma consulta do painel, com o recorte por atendimento. */
function paramsDe(janela: Janela, extras: Record<string, unknown>) {
  return janela.agentId ? { ...extras, agent_id: janela.agentId } : extras
}

function desde(dias: number): string {
  const d = new Date()
  d.setDate(d.getDate() - dias)
  return d.toISOString()
}

function dataDe(dias: number): string {
  return desde(dias).slice(0, 10)
}

/* -------------------------------------------------------------------------- */
/* Execuções                                                                   */
/* -------------------------------------------------------------------------- */

export interface FiltroDeExecucoes {
  status?: string
  desdeDias?: number
  limit?: number
  offset?: number
  /** Filtros do funil (spec 117), combináveis entre si. */
  etapaAlcancada?: number
  motivo?: string
  canal?: string
  comTransbordo?: boolean
  objetivo?: string
  /**
   * Conversas ainda abertas. SUBSTITUI `status` quando a unidade é conversa:
   * `status=running` quer dizer "existe um turno rodando neste instante", e
   * um turno dura de 2 a 21 s — numa lista de conversas aquele filtro nunca
   * devolve nada.
   */
  conversaAberta?: boolean
  /** O hash da pergunta sem resposta, vindo do ranking (`chave`). */
  pergunta?: string
}

/**
 * Os parâmetros de filtro, os MESMOS nas duas rotas (turnos e atendimentos):
 * o backend monta as duas pela mesma função, e a tela também.
 */
function parametrosDoFiltro(filtro: FiltroDeExecucoes): Record<string, string | number> {
  const params: Record<string, string | number> = {
    limit: filtro.limit ?? 50,
    offset: filtro.offset ?? 0,
  }
  if (filtro.status) params.status = filtro.status
  if (filtro.desdeDias) params.since = desde(filtro.desdeDias)
  // Os filtros do funil (spec 117), combináveis entre si.
  if (filtro.etapaAlcancada != null) params.etapa_alcancada = filtro.etapaAlcancada
  if (filtro.motivo) params.motivo = filtro.motivo
  if (filtro.canal) params.canal = filtro.canal
  if (filtro.comTransbordo != null) params.com_transbordo = String(filtro.comTransbordo)
  if (filtro.objetivo) params.objetivo = filtro.objetivo
  if (filtro.conversaAberta != null)
    params.conversa_aberta = String(filtro.conversaAberta)
  if (filtro.pergunta) params.pergunta = filtro.pergunta
  return params
}

export const execucoesService = {
  async listar(
    organizationId: string,
    filtro: FiltroDeExecucoes = {}
  ): Promise<OrganizationExecutionList> {
    const { data } = await api.get(`/organizations/${organizationId}/executions`, {
      params: parametrosDoFiltro(filtro),
    })
    return organizationExecutionListSchema.parse(data)
  },

  /**
   * A lista agrupada por atendimento (spec 128): uma linha por sessão e ciclo,
   * com o bloco do atendimento. A página é de GRUPOS.
   */
  async listarAtendimentos(
    organizationId: string,
    filtro: Omit<FiltroDeExecucoes, 'limit' | 'offset'> = {},
    pagina: { limit: number; offset: number } = { limit: 50, offset: 0 }
  ): Promise<AttendanceListResponse> {
    const { data } = await api.get(
      `/organizations/${organizationId}/executions/attendances`,
      { params: parametrosDoFiltro({ ...filtro, ...pagina }) }
    )
    return attendanceListResponseSchema.parse(data)
  },

  /** Abre uma execução: o que ela sabe, o que a conversa sabe, e sob o quê. */
  async abrir(executionId: string): Promise<ExecucaoAberta> {
    const { data } = await api.get(`/executions/${executionId}/aberta`)
    return execucaoAbertaSchema.parse(data)
  },

  async cancelar(executionId: string): Promise<void> {
    await api.post(`/executions/${executionId}/cancel`)
  },
}

/* -------------------------------------------------------------------------- */
/* Custos                                                                      */
/* -------------------------------------------------------------------------- */

export const custosService = {
  async daOrganizacao(
    organizationId: string,
    janela: Janela
  ): Promise<OrganizationCostSummary> {
    const { data } = await api.get(`/organizations/${organizationId}/costs`, {
      params: { date_from: dataDe(janela.dias) },
    })
    return organizationCostSummarySchema.parse(data)
  },

  /** A série diária vem das métricas do atendimento, do livro-razão. */
  async porDia(organizationId: string, janela: Janela): Promise<Ponto[]> {
    const { data } = await api.get(
      `/organizations/${organizationId}/metricas/custo`,
      { params: paramsDe(janela, { inicio: desde(janela.dias) }) }
    )
    return z.array(pontoSchema).parse(data)
  },
}

/* -------------------------------------------------------------------------- */
/* Desempenho do atendimento                                                   */
/* -------------------------------------------------------------------------- */

export const desempenhoService = {
  async turnosPorDia(organizationId: string, janela: Janela): Promise<Ponto[]> {
    const { data } = await api.get(
      `/organizations/${organizationId}/metricas/turnos`,
      { params: paramsDe(janela, { inicio: desde(janela.dias) }) }
    )
    return z.array(pontoSchema).parse(data)
  },

  async conversas(organizationId: string, janela: Janela): Promise<Conversa[]> {
    const { data } = await api.get(
      `/organizations/${organizationId}/metricas/custo/conversas`,
      { params: paramsDe(janela, { inicio: desde(janela.dias), limite: 500 }) }
    )
    return z.array(conversaSchema).parse(data)
  },

  async transbordos(organizationId: string, janela: Janela): Promise<Fatia[]> {
    const { data } = await api.get(
      `/organizations/${organizationId}/metricas/transbordos`,
      { params: paramsDe(janela, { inicio: desde(janela.dias) }) }
    )
    return z.array(fatiaSchema).parse(data)
  },

  async funil(organizationId: string, janela: Janela): Promise<Conversao[]> {
    const { data } = await api.get(
      `/organizations/${organizationId}/metricas/funil/conversao`,
      { params: paramsDe(janela, { inicio: desde(janela.dias) }) }
    )
    return z.array(conversaoSchema).parse(data)
  },

  /** Até onde as conversas chegaram, etapa por etapa (spec 117). */
  async cascata(organizationId: string, janela: Janela): Promise<Cascata> {
    const { data } = await api.get(
      `/organizations/${organizationId}/metricas/funil/cascata`,
      { params: paramsDe(janela, { inicio: desde(janela.dias) }) }
    )
    return cascataSchema.parse(data)
  },

  async vazamento(organizationId: string, janela: Janela): Promise<Fatia[]> {
    const { data } = await api.get(
      `/organizations/${organizationId}/metricas/funil/vazamento`,
      { params: paramsDe(janela, { inicio: desde(janela.dias) }) }
    )
    return z.array(fatiaSchema).parse(data)
  },

  async motivos(organizationId: string, janela: Janela): Promise<Motivo[]> {
    const { data } = await api.get(
      `/organizations/${organizationId}/metricas/funil/motivos`,
      { params: paramsDe(janela, { inicio: desde(janela.dias) }) }
    )
    return z.array(motivoSchema).parse(data)
  },

  async perguntas(organizationId: string, janela: Janela): Promise<Pergunta[]> {
    const { data } = await api.get(
      `/organizations/${organizationId}/metricas/funil/perguntas`,
      { params: paramsDe(janela, { inicio: desde(janela.dias) }) }
    )
    return z.array(perguntaSchema).parse(data)
  },

  /**
   * A seção de perguntas sem resposta se aplica a este escritório?
   *
   * Vem do cadastro de ferramentas do agente, nunca de contar linhas: lista
   * vazia por acerto e lista vazia por gatilho quebrado têm a mesma aparência.
   */
  async aplicabilidade(organizationId: string): Promise<Aplicabilidade> {
    const { data } = await api.get(
      `/organizations/${organizationId}/metricas/funil/aplicabilidade`
    )
    return aplicabilidadeSchema.parse(data)
  },
}

/* -------------------------------------------------------------------------- */
/* O agente que atende, e o documento dele                                     */
/* -------------------------------------------------------------------------- */

export const atendimentoService = {
  async agente(organizationId: string): Promise<AgenteDeAtendimento> {
    const { data } = await api.get(
      `/organizations/${organizationId}/atendimento/agente`
    )
    return agenteDeAtendimentoSchema.parse(data)
  },

  /** Uma linha por atendimento da organização (spec 157, FR-012). */
  async agentes(organizationId: string): Promise<Atendimento[]> {
    const { data } = await api.get(
      `/organizations/${organizationId}/atendimento/agentes`
    )
    return z.array(atendimentoSchema).parse(data)
  },

  /** As caixas da conta do canal, com o agente de cada uma (spec 157). */
  async caixas(organizationId: string): Promise<CaixasDaConta> {
    const { data } = await api.get(
      `/organizations/${organizationId}/atendimento/caixas`
    )
    return caixasResponseSchema.parse(data)
  },

  /** Liga a caixa (ou qualquer caixa, com `inboxId` nulo) ao agente. */
  async ligarCaixa(
    organizationId: string,
    inboxId: number | null,
    agentId: string
  ): Promise<VinculoDeCaixa> {
    const { data } = await api.put(
      `/organizations/${organizationId}/atendimento/caixas`,
      { inbox_id: inboxId, agent_id: agentId }
    )
    return vinculoDeCaixaSchema.parse(data)
  },

  /** Desliga o vínculo: a caixa passa a terminar em transbordo. */
  async desligarCaixa(
    organizationId: string,
    linkId: string
  ): Promise<VinculoDeCaixa> {
    const { data } = await api.delete(
      `/organizations/${organizationId}/atendimento/caixas/${linkId}`
    )
    return vinculoDeCaixaSchema.parse(data)
  },

  /** Os times da conta do canal, com o número de pessoas (FR-019). */
  async times(organizationId: string): Promise<TimeDoCanal[]> {
    const { data } = await api.get(
      `/organizations/${organizationId}/atendimento/times`
    )
    return z.array(timeDoCanalSchema).parse(data)
  },
}

/* -------------------------------------------------------------------------- */
/* Blueprint                                                                   */
/* -------------------------------------------------------------------------- */

/** As sub-rotas dos documentos, iguais nos três prefixos (spec 157). */
function blueprintsEm(prefixo: (organizationId: string) => string) {
  return {
    /** O JSON Schema deste escopo, com `editavel_por` por campo. */
    async schema(organizationId: string): Promise<Record<string, unknown>> {
      const { data } = await api.get(`${prefixo(organizationId)}/schema`)
      return data as Record<string, unknown>
    },

    async versoes(organizationId: string): Promise<BlueprintVersao[]> {
      const { data } = await api.get(prefixo(organizationId))
      return z.array(blueprintVersaoSchema).parse(data)
    },

    async versao(
      organizationId: string,
      version: number
    ): Promise<Record<string, unknown>> {
      const { data } = await api.get(`${prefixo(organizationId)}/${version}`)
      return data as Record<string, unknown>
    },

    async criarRascunho(
      organizationId: string,
      spec: Record<string, unknown>
    ): Promise<BlueprintVersao> {
      const { data } = await api.post(`${prefixo(organizationId)}/drafts`, { spec })
      return blueprintVersaoSchema.parse(data)
    },

    async validar(organizationId: string, version: number): Promise<Validacao> {
      const { data } = await api.post(
        `${prefixo(organizationId)}/drafts/${version}/validate`
      )
      return validacaoSchema.parse(data)
    },

    async publicar(organizationId: string, version: number): Promise<BlueprintVersao> {
      const { data } = await api.post(
        `${prefixo(organizationId)}/drafts/${version}/publish`
      )
      return blueprintVersaoSchema.parse(data)
    },

    async reverter(organizationId: string): Promise<BlueprintVersao> {
      const { data } = await api.post(`${prefixo(organizationId)}/revert`)
      return blueprintVersaoSchema.parse(data)
    },

    async cenarios(organizationId: string): Promise<Cenario[]> {
      const { data } = await api.get(`${prefixo(organizationId)}/scenarios`)
      return z.array(cenarioSchema).parse(data)
    },
  }
}

/** O documento da organização (ou o único, enquanto ela não foi dividida). */
export const blueprintService = {
  ...blueprintsEm((org) => `/organizations/${org}/blueprints`),
  /** O documento de UM atendimento (prefixo do agente). */
  doAgente(agentId: string) {
    return blueprintsEm((org) => `/organizations/${org}/agents/${agentId}/blueprints`)
  },
}
